/**
 * Plant Doctor: reads the whole plant (its history, every photo and note, every
 * day of its weather) to diagnose and solve problems. A real case leads: the
 * owner's romaine lettuce photo (Sep 28, bolting in the heat) and the Plant
 * Doctor answer it got (public/landing/plant-doctor-photo.jpg,
 * plant-doctor-answer.jpg),
 * one above the other on a phone so the answer can be read, side by side on
 * a wide screen. Beside them, the gardener's pain, the heading, the body (two
 * short sentences) and the "Checkup" button.
 *
 * The words say "solve"; nothing here promises it is always right.
 *
 * Words (en/es): landing.plantDoctor.*.
 *
 * @module PlantDoctorSection
 */

import React from 'react';
import { Stethoscope } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { FeatureBlock } from '../components/FeatureBlock';
import type { LandingSectionProps } from '../landingTypes';

/**
 * The Plant Doctor section.
 *
 * @param props.facts - The founding welcome, when there is one (a founder is offered the founding claim, not a trial).
 * @param props.onStart - Starts a garden (the "Checkup" button).
 */
export const PlantDoctorSection: React.FC<LandingSectionProps> = ({ facts, onStart }) => {
  const { reactTranslate: t } = useTranslation();
  return (
    <FeatureBlock
      icon={<Stethoscope className="h-8 w-8" strokeWidth={1.75} />}
      pain={t('landing.plantDoctor.pain')}
      title={t('landing.plantDoctor.title')}
      line={t('landing.plantDoctor.body')}
      action={<Button size="lg" onClick={onStart}>{t(facts.startKey ?? 'landing.plantDoctor.cta')}</Button>}
      wide
      evidence={(
        <div className="grid grid-cols-1 items-start gap-3 sm:grid-cols-2">
          <img src="/landing/plant-doctor-photo.jpg" alt={t('landing.plantDoctor.photoAlt')} loading="lazy" className="aspect-[3/4] w-full rounded-2xl object-cover shadow-xl" />
          <img src="/landing/plant-doctor-answer.jpg" alt={t('landing.plantDoctor.answerAlt')} loading="lazy" className="w-full rounded-2xl border shadow-xl" />
        </div>
      )}
    />
  );
};
